import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useAdminUpgradeReviewDietsQuery,
  useAdminGradeUpgradesQuery,
  useAdminCreateUpgradeReviewDietMutation,
  useAdminUpdateUpgradeReviewDietMutation,
  useAdminDeleteUpgradeReviewDietMutation,
} from "../api/upgradeApi";

type Diet = {
  id: string;
  title: string;
  start_date?: string | null;
  end_date?: string | null;
  /** The date this cycle's upgrades take effect from. Set on the cycle page. */
  conferred_on?: string | null;
  status?: string;
  is_active?: boolean;
  notes?: string | null;
  applications_count?: number;
  members_count?: number;
  pending_reviews_count?: number;
};

function StatusBadge({ status }: { status?: string }) {
  const map: Record<string, string> = {
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    ongoing: "bg-emerald-50 text-emerald-700 border-emerald-200",
    closed: "bg-gray-100 text-gray-600 border-gray-200",
  };
  const cls = map[status || ""] || "bg-gray-100 text-gray-600 border-gray-200";
  return (
    <span className={`inline-flex text-[11px] font-bold px-2 py-0.5 rounded-full border ${cls}`}>
      {status || "—"}
    </span>
  );
}

/**
 * Review cycles for grade upgrades — the upgrade counterpart of
 * /admin/logbook-assessment. A cycle holds the applications one sitting of the
 * Membership Committee is deciding, plus the panel deciding them.
 */
export default function AdminUpgradeReviewDietsPage() {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useAdminUpgradeReviewDietsQuery();
  const allDiets: Diet[] = useMemo(
    () => (Array.isArray(data?.data) ? data.data : []),
    [data],
  );

  /* ── Grade filter ─────────────────────────────────────────────────────────
     Arrived at from a category page (Upgrades to Member → Review cycles), so
     the screen opens on that grade's work instead of every grade at once.

     A cycle itself is NOT tied to a grade — one sitting of the Membership
     Committee decides Members and Fellows together — so this filters what is
     shown rather than changing what a cycle is. Saying so plainly on the page
     matters: a cycle that looks like "the Member cycle" but silently holds
     Fellows would mislead whoever closes it. */
  const [params, setParams] = useSearchParams();
  const categoryId = params.get("category") ?? "";
  const gradeParam = params.get("grade") ?? "";

  // The cycle list carries no grade breakdown, so the applications themselves
  // are read to work out how much of each cycle belongs to this grade.
  //
  // `include_closed_cycles` because the admin listing now leaves out work that
  // was treated in a closed cycle — right for the category screen, fatal here:
  // every closed cycle would count zero of this grade and then be hidden by the
  // filter below, which is exactly the cycle holding the applications.
  const { data: appsData, isFetching: countingApps } = useAdminGradeUpgradesQuery(
    { to_category_id: categoryId, per_page: 500, include_closed_cycles: true },
    { skip: !categoryId },
  );

  const appRows: any[] = useMemo(
    () => (Array.isArray(appsData?.data) ? appsData.data : []),
    [appsData],
  );

  const grade = gradeParam || appRows[0]?.to_category?.name || "this grade";

  /** How many of THIS grade's applications each cycle is holding. */
  const gradeCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of appRows) {
      const dietId = row?.upgrade_review_diet_id;
      if (dietId) counts.set(String(dietId), (counts.get(String(dietId)) || 0) + 1);
    }
    return counts;
  }, [appRows]);

  // An escape hatch, because the filter can only ever hide closed cycles (see
  // below) and someone will eventually want the one it hid.
  const [showAll, setShowAll] = useState(false);

  // Nothing is hidden until the counts are actually in. Filtering on an empty
  // map would drop every closed cycle for a moment and then put some of them
  // back, which reads as the list glitching rather than as it loading.
  const filtering = !!categoryId && !showAll && appsData !== undefined;

  /* A closed cycle holding none of this grade is not this grade's business, so
     it goes. An OPEN one is kept whatever it holds — it is where the next batch
     of this grade's work gets pulled in, and a cycle you cannot see is a cycle
     you cannot add to. */
  const diets = useMemo(
    () =>
      filtering
        ? allDiets.filter((d) => (gradeCounts.get(String(d.id)) ?? 0) > 0 || d.status !== "closed")
        : allDiets,
    [allDiets, filtering, gradeCounts],
  );

  const hidden = allDiets.length - diets.length;

  // The API server currently answers errors with HTTP 200 and an error body, so
  // RTK Query reports success and the page would sit there empty with nothing
  // said. Anything that isn't a list is a failure — show what the server said.
  const serverError =
    data && !Array.isArray(data?.data)
      ? String(data?.message || "The server did not return a list of review cycles.")
      : null;

  const [createDiet, { isLoading: creating }] = useAdminCreateUpgradeReviewDietMutation();
  const [updateDiet] = useAdminUpdateUpgradeReviewDietMutation();
  const [deleteDiet] = useAdminDeleteUpgradeReviewDietMutation();

  // One cycle sits at a time — the server refuses a second, and the button is
  // disabled here so the refusal isn't the first the admin hears of it. Read
  // from every cycle, not the filtered view: the rule is Institute-wide, and a
  // grade filter must not make a live cycle look absent.
  const sitting = allDiets.find((d) => d.status === "ongoing" || d.is_active);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", start_date: "", end_date: "", notes: "" });
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setErr(null);
    setOk(null);
    if (!form.title.trim()) {
      setErr("Please enter a title.");
      return;
    }
    try {
      const res: any = await createDiet({
        title: form.title.trim(),
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        notes: form.notes || null,
      }).unwrap();

      // A 200 without the created record is a failure the server dressed up as
      // success — reporting "created" would be a lie the empty list contradicts.
      if (!res?.data?.id) {
        setErr(String(res?.message || "The server did not create the review cycle."));
        return;
      }

      setOk("Review cycle created.");
      setForm({ title: "", start_date: "", end_date: "", notes: "" });
      setOpen(false);
    } catch (e: any) {
      setErr(String(e?.data?.message || e?.message || "Failed to create the review cycle."));
    }
  };

  // Starting a cycle closes any other sitting one — the server enforces the
  // same rule, this just states it in the confirmation.
  //
  // The server's own message wins where it has one: closing a cycle also hands
  // back any application the committee never decided, and how many went back is
  // something the admin has to be told rather than left to notice.
  const runAction = async (fn: () => Promise<any>, successMsg: string) => {
    setErr(null);
    setOk(null);
    try {
      const res: any = await fn();
      setOk(String(res?.message || successMsg));
    } catch (e: any) {
      setErr(String(e?.data?.message || e?.message || "Action failed."));
    }
  };

  return (
    <div className="bg-white border rounded-xl p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-800">
            Upgrade Review Cycles
            {categoryId ? <span className="text-gray-400 font-semibold"> · {grade}</span> : null}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Create a review cycle, seat the Membership Committee panel on it, and assign upgrade
            applications to the panel members.
          </p>
          {categoryId ? (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-700 text-xs font-bold">
                Showing {grade}
                <button
                  onClick={() => setParams({})}
                  title="Show every grade"
                  className="hover:text-indigo-900"
                >
                  ✕
                </button>
              </span>
              {/* Said out loud rather than implied by the filter: a cycle is not
                  a grade's cycle, it is a sitting that happens to hold some of
                  that grade's work. */}
              <span className="text-xs text-gray-500">
                Cycles are not tied to a grade — each can hold applications of any grade. This
                only narrows what is listed and counted.
              </span>
            </div>
          ) : null}
          {sitting ? (
            <p className="text-xs font-semibold text-emerald-700 mt-1">
              “{sitting.title}” is ongoing — close it before another cycle can be started.
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() =>
              navigate(
                categoryId
                  ? `/admin/upgrade-applications/category/${categoryId}`
                  : "/admin/upgrade-applications",
              )
            }
            className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
          >
            {categoryId ? `Back to ${grade} applications` : "Back to applications"}
          </button>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
          >
            Refresh
          </button>
          <button
            onClick={() => {
              setErr(null);
              setOk(null);
              setOpen(true);
            }}
            className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-sm"
          >
            New Review Cycle
          </button>
        </div>
      </div>

      {err || serverError ? (
        <div className="mt-4 text-xs text-red-700 bg-red-50 border border-red-100 rounded p-3">
          {err || serverError}
        </div>
      ) : null}
      {ok ? (
        <div className="mt-4 text-xs text-green-700 bg-green-50 border border-green-100 rounded p-3">{ok}</div>
      ) : null}

      <div className="mt-6 overflow-x-auto">
        {isLoading ? (
          <div className="text-gray-600 py-8">Loading review cycles…</div>
        ) : isError ? (
          <div className="text-red-600 py-8">Failed to load review cycles.</div>
        ) : !diets.length ? (
          <div className="text-gray-500 py-8">
            {categoryId && allDiets.length
              ? `No review cycle holds any ${grade} application.`
              : "No review cycles yet. Create one to get started."}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2">Title</th>
                <th className="py-2">Period</th>
                <th className="py-2">Status</th>
                <th className="py-2">Panel</th>
                {/* The grade's own share, beside the cycle's total — the two
                    together are what says whether this sitting is worth opening
                    for the category you came from. */}
                {categoryId ? <th className="py-2">{grade}</th> : null}
                <th className="py-2">Applications</th>
                <th className="py-2">Pending reviews</th>
                <th className="py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {diets.map((d) => (
                <tr key={d.id} className="border-b hover:bg-gray-50">
                  <td className="py-3 font-semibold text-gray-800">
                    {d.title}
                    {d.is_active ? (
                      <span className="ml-2 text-[10px] font-bold text-emerald-700">ACTIVE</span>
                    ) : null}
                  </td>
                  <td className="py-3 text-gray-600">
                    {d.start_date || "—"} → {d.end_date || "—"}
                    {/* Named here too: it is the one date that stops the
                        upgrade, and finding out on the cycle page is a wasted
                        trip. */}
                    {d.conferred_on ? (
                      <div className="text-[11px] text-gray-400">
                        Conferred {d.conferred_on}
                      </div>
                    ) : (
                      <div className="text-[11px] text-amber-600 font-semibold">
                        No date conferred
                      </div>
                    )}
                  </td>
                  <td className="py-3">
                    <StatusBadge status={d.status} />
                  </td>
                  <td className="py-3">{d.members_count ?? 0}</td>
                  {categoryId ? (
                    <td className="py-3">
                      {countingApps ? (
                        <span className="text-gray-300">…</span>
                      ) : (gradeCounts.get(String(d.id)) ?? 0) > 0 ? (
                        <span className="px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 font-bold text-xs tabular-nums">
                          {gradeCounts.get(String(d.id))}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  ) : null}
                  <td className="py-3">{d.applications_count ?? 0}</td>
                  <td className="py-3">{d.pending_reviews_count ?? 0}</td>
                  <td className="py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() =>
                          navigate({
                            pathname: `/admin/upgrade-applications/review-cycles/${d.id}`,
                            // The grade travels on, so the cycle opens on the
                            // same slice of work this list was showing.
                            search: params.toString(),
                          })
                        }
                        className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-xs"
                      >
                        Manage
                      </button>

                      {d.status !== "closed" ? (
                        d.status === "ongoing" ? (
                          <button
                            onClick={() => {
                              // Closing is the one action with consequences
                              // outside the cycle, so it says them first.
                              if (
                                !window.confirm(
                                  `Close "${d.title}"?\n\n` +
                                    "No more applications can be added to it or handed out in it, and the " +
                                    "work it decided drops off the upgrade applications list — the cycle " +
                                    "becomes where those applications are read.\n\n" +
                                    "Anything the committee never decided goes back to the pool for the " +
                                    "next sitting.",
                                )
                              )
                                return;
                              runAction(
                                () =>
                                  updateDiet({
                                    id: d.id,
                                    title: d.title,
                                    start_date: d.start_date || null,
                                    end_date: d.end_date || null,
                                    notes: d.notes || null,
                                    status: "closed",
                                    is_active: false,
                                  }).unwrap(),
                                "Review cycle closed.",
                              );
                            }}
                            className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-xs"
                          >
                            Close
                          </button>
                        ) : (
                          <button
                            onClick={() =>
                              runAction(
                                () =>
                                  updateDiet({
                                    id: d.id,
                                    title: d.title,
                                    start_date: d.start_date || null,
                                    end_date: d.end_date || null,
                                    notes: d.notes || null,
                                    status: "ongoing",
                                    is_active: true,
                                  }).unwrap(),
                                "Review cycle is now ongoing.",
                              )
                            }
                            disabled={!!sitting}
                            title={
                              sitting
                                ? `Close "${sitting.title}" before starting another cycle.`
                                : undefined
                            }
                            className="px-3 py-1.5 rounded-lg border hover:bg-gray-50 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            Start
                          </button>
                        )
                      ) : null}

                      {/* Deleting a cycle that holds applications would strip
                          the sitting off every record pointing at it, so it is
                          only offered while the cycle is empty. */}
                      <button
                        onClick={() => {
                          if (window.confirm(`Delete review cycle "${d.title}"?`)) {
                            runAction(() => deleteDiet(d.id).unwrap(), "Review cycle deleted.");
                          }
                        }}
                        disabled={(d.applications_count ?? 0) > 0}
                        title={
                          (d.applications_count ?? 0) > 0
                            ? "Applications are assigned to this cycle — remove them first."
                            : undefined
                        }
                        className="px-3 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-xs disabled:opacity-40 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* What the filter took away, and how to get it back. A list that
            quietly drops rows reads as a complete list. */}
        {hidden > 0 ? (
          <p className="mt-3 text-xs text-gray-500">
            {hidden} closed cycle{hidden === 1 ? "" : "s"} with no {grade} application
            {hidden === 1 ? " is" : "s are"} hidden.{" "}
            <button onClick={() => setShowAll(true)} className="underline font-semibold">
              Show all cycles
            </button>
          </p>
        ) : null}
        {showAll && categoryId ? (
          <p className="mt-3 text-xs text-gray-500">
            Showing every cycle, including those with no {grade} application.{" "}
            <button onClick={() => setShowAll(false)} className="underline font-semibold">
              Back to {grade} only
            </button>
          </p>
        ) : null}
      </div>

      {open ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white border shadow-lg p-5">
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-lg font-semibold">New Review Cycle</h2>
              <button onClick={() => setOpen(false)} className="px-2 py-1 rounded border hover:bg-gray-50">
                ✕
              </button>
            </div>

            {err ? (
              <div className="mt-3 text-xs text-red-700 bg-red-50 border border-red-100 rounded p-3">
                {err}
              </div>
            ) : null}

            {/* Arrived here from a grade's page, but the cycle being created is
                not that grade's cycle. Said now rather than discovered later,
                when Fellows turn up in what was titled a Member sitting. */}
            {categoryId ? (
              <div className="mt-3 text-xs text-gray-600 bg-gray-50 border rounded p-3">
                This cycle will not be tied to {grade} — any grade's applications can be pulled
                into it. Name it accordingly if it is meant for one grade's sitting.
              </div>
            ) : null}

            <div className="mt-4 space-y-3">
              <div>
                <label className="text-xs font-semibold text-gray-600">Title</label>
                <input
                  value={form.title}
                  onChange={(e) => set("title", e.target.value)}
                  placeholder="e.g. 2026 Q3 Upgrade Review"
                  className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600">Start date</label>
                  <input
                    type="date"
                    value={form.start_date}
                    onChange={(e) => set("start_date", e.target.value)}
                    className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">End date</label>
                  <input
                    type="date"
                    value={form.end_date}
                    onChange={(e) => set("end_date", e.target.value)}
                    className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600">Notes (optional)</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => set("notes", e.target.value)}
                  rows={3}
                  className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setOpen(false)}
                className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
                disabled={creating}
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={creating}
                className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-sm disabled:opacity-60"
              >
                {creating ? "Creating…" : "Create"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
